// AdminDocumentsView.js - Component for Admin Dashboard to view all documents

import React, { useState, useEffect } from 'react';
import { api, API_BASE } from '../services/api';
import { FaFolder, FaEye, FaDownload, FaTrash, FaSearch, FaFilter, FaChevronDown } from 'react-icons/fa';

const AdminDocumentsView = ({ selectedRegNumber }) => {
  const [documents, setDocuments] = useState([]);
  const [groupedDocs, setGroupedDocs] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'list'
  const [expandedFolders, setExpandedFolders] = useState({}); // Track expanded state

  const documentTypes = [
    { value: '', label: 'All Types' },
    { value: 'registration', label: '🚗 Vehicle Registration' },
    { value: 'insurance', label: '🛡️ Insurance Document' },
    { value: 'mot', label: '✅ MOT Certificate' },
    { value: 'service', label: '🔧 Service Record' },
    { value: 'invoice', label: '💰 Invoice/Receipt' },
    { value: 'damage_photo', label: '📸 Damage Photo' },
    { value: 'owner_id', label: '🪪 Owner ID Proof' },
    { value: 'other', label: '📄 Other Document' }
  ];

  // Load documents
  const loadDocuments = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterType) filters.docType = filterType;

      const docs = await api.getUploadedDocuments(filters);
      setDocuments(docs || []);

      // Group by registration
      const grouped = (docs || []).reduce((acc, doc) => {
        const reg = doc.regNumber;
        if (!acc[reg]) acc[reg] = [];
        acc[reg].push(doc);
        return acc;
      }, {});

      setGroupedDocs(grouped);
    } catch (error) {
      setMessage(`❌ Failed to load documents: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const stats = await api.getDocumentStats();
      setStats(stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadDocuments();
    loadStats();
  }, [filterType]);

  // Auto-open folder when selectedRegNumber is provided
  useEffect(() => {
    if (selectedRegNumber) {
      setViewMode('grouped');

      setExpandedFolders(prev => ({
        ...prev,
        [selectedRegNumber]: true
      }));

      setTimeout(() => {
        const folderElement = document.getElementById(`folder-${selectedRegNumber}`);
        if (folderElement) {
          folderElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          folderElement.classList.add('highlighted');
          setTimeout(() => folderElement.classList.remove('highlighted'), 2000);
        }
      }, 100);
    }
  }, [selectedRegNumber]);

  // Handle delete
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.deleteDocument(docId);
      setMessage('✅ Document deleted successfully');
      loadDocuments();
      loadStats();
    } catch (error) {
      setMessage(`❌ Delete failed: ${error.message}`);
    }
  };

  // Handle view
  const handleView = (doc) => {
    // ✅ Use API_BASE from api.js (same as Staff Dashboard)
    const fullUrl = doc.url.startsWith('http')
      ? doc.url
      : `${API_BASE}${doc.url}`;

    console.log('📂 Opening document:', fullUrl);
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  // Filter documents by search term
  const filteredDocs = documents.filter(doc => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      doc.regNumber.toLowerCase().includes(term) ||
      doc.fileName.toLowerCase().includes(term) ||
      doc.uploadedByName?.toLowerCase().includes(term)
    );
  });

  // Filter grouped docs
  const filteredGrouped = Object.entries(groupedDocs).filter(([reg]) => {
    if (!searchTerm) return true;
    return reg.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Toggle folder expansion
  const toggleFolder = (reg) => {
    setExpandedFolders(prev => ({
      ...prev,
      [reg]: !prev[reg]
    }));
  };

  return (
    <section className="admin-section documents-view">
      <div className="section-header">
        <h2>📂 Uploaded Documents</h2>
        <div className="header-actions">
          <button
            className={`btn ${viewMode === 'grouped' ? 'btn-primary' : ''}`}
            onClick={() => setViewMode('grouped')}
          >
            <FaFolder /> Grouped View
          </button>
          <button
            className={`btn ${viewMode === 'list' ? 'btn-primary' : ''}`}
            onClick={() => setViewMode('list')}
          >
            📋 List View
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📄</div>
            <div className="stat-info">
              <div className="stat-value">{stats.totalDocuments}</div>
              <div className="stat-label">Total Documents</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🚗</div>
            <div className="stat-info">
              <div className="stat-value">{stats.uniqueRegistrations}</div>
              <div className="stat-label">Vehicle Registrations</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-value">
                {stats.documentsByType?.length || 0}
              </div>
              <div className="stat-label">Document Types</div>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`admin-message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Filters */}
      <div className="filters-row">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by registration, filename, or uploader..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-box">
          <FaFilter />
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            {documentTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <button className="btn" onClick={loadDocuments}>
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading documents...</div>
      ) : viewMode === 'grouped' ? (
        // GROUPED VIEW
        <div className="grouped-view">
          {filteredGrouped.length === 0 ? (
            <div className="empty-state">
              <FaFolder size={50} color="#ccc" />
              <p>No documents found</p>
            </div>
          ) : (
            filteredGrouped.map(([reg, docs]) => (
              <div key={reg} id={`folder-${reg}`} className="registration-folder">
                <div
                  className={`folder-header ${!expandedFolders[reg] ? 'collapsed' : ''}`}
                  onClick={() => toggleFolder(reg)}
                >
                  <FaChevronDown className="collapse-icon" size={16} />
                  <FaFolder color="#FFB800" size={24} />
                  <h3>{reg}</h3>
                  <span className="doc-count">{docs.length} document(s)</span>
                </div>
                <div className={`folder-contents ${!expandedFolders[reg] ? 'collapsed' : ''}`}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>File Name</th>
                        <th>Uploaded By</th>
                        <th>Date</th>
                        <th>Size</th>
                        <th>Actions</th>
                      </tr >
                    </thead >
                    <tbody>
                      {docs.map(doc => (
                        <tr key={doc._id}>
                          <td>
                            <span className="doc-type-badge">
                              {documentTypes.find(t => t.value === doc.docType)?.label || doc.docType}
                            </span>
                          </td>
                          <td className="file-name-cell">
                            {doc.fileType === 'pdf' ? '📄' : '🖼️'} {doc.fileName}
                          </td>
                          <td>{doc.uploadedByName || doc.uploadedBy}</td>
                          <td>{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                          <td>{(doc.fileSize / 1024).toFixed(2)} KB</td>
                          <td>
                            <button
                              className="btn-icon view-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(doc);
                              }}
                              title="View Document"
                              type="button"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="btn-icon delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(doc._id);
                              }}
                              title="Delete Document"
                              type="button"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table >
                </div >
              </div >
            ))
          )}
        </div >
      ) : (
        // LIST VIEW
        <div className="list-view">
          {filteredDocs.length === 0 ? (
            <div className="empty-state">
              <p>No documents found</p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Registration</th>
                  <th>Type</th>
                  <th>File Name</th>
                  <th>Uploaded By</th>
                  <th>Date</th>
                  <th>Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map(doc => (
                  <tr key={doc._id}>
                    <td><strong>{doc.regNumber}</strong></td>
                    <td>
                      <span className="doc-type-badge">
                        {documentTypes.find(t => t.value === doc.docType)?.label || doc.docType}
                      </span>
                    </td>
                    <td className="file-name-cell">
                      {doc.fileType === 'pdf' ? '📄' : '🖼️'} {doc.fileName}
                    </td>
                    <td>{doc.uploadedByName || doc.uploadedBy}</td>
                    <td>{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                    <td>{(doc.fileSize / 1024).toFixed(2)} KB</td>
                    <td>
                      <button
                        className="btn-icon view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(doc);
                        }}
                        title="View Document"
                        type="button"
                      >
                        <FaEye />
                      </button>
                      <button
                        className="btn-icon delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(doc._id);
                        }}
                        title="Delete Document"
                        type="button"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section >
  );
};

export default AdminDocumentsView;
